// PartWizard — the per-part wizard container for ai-quote 2.0 M2 Phase 5.3.
//
// Per spec/14 + v8 mockup section 3, this is the central UI for assembling
// a Part's full spec one step at a time. The wizard:
//   1. Picks the right set of steps for the part's type (PART_TYPE_META.wizardSteps)
//   2. Renders one step at a time with a numbered StepDots indicator
//   3. Lets the user navigate Back / Save-as-draft / Next, and jump between
//      dots (per spec/14 open-question Q2: jump allowed)
//   4. On the Review step, exposes Save which calls onSave(part)
//
// Designed to be reused: the NewPartModal will embed this for new parts,
// and the Quote builder will embed it inline when editing a part on a quote.
// `embedded` prop removes the outer card-frame so it sits cleanly inside
// another card.

import { useMemo, useState } from 'react';
import type { Part, PartType } from '@/shared/crmTypes';
import { PART_TYPE_META } from '@/components/PartTypeBadge';
import { PartTypeBadge } from '@/components/PartTypeBadge';
import type { StepProps } from './stepTypes';

import { IdentityStep } from './steps/IdentityStep';
import { MaterialStep } from './steps/MaterialStep';
import { SetupsStep } from './steps/SetupsStep';
import { SheetStockStep } from './steps/SheetStockStep';
import { DxfStep } from './steps/DxfStep';
import { SubPartsStep } from './steps/SubPartsStep';
import { WeldOpsStep } from './steps/WeldOpsStep';
import { SecondaryOpsStep } from './steps/SecondaryOpsStep';
import { FinishingStep } from './steps/FinishingStep';
import { SourceStep } from './steps/SourceStep';
import { MarkupStep } from './steps/MarkupStep';
import { ReviewStep } from './steps/ReviewStep';
import { CollarNestingStep } from './steps/CollarNestingStep';
import { CollarCutStep } from './steps/CollarCutStep';
import { OutsideServicesStep } from './steps/OutsideServicesStep';

// Step ids are stable strings; they map to step-name labels (per
// PART_TYPE_META.wizardSteps) and to React components.
type StepId =
  | 'identity'
  | 'material'
  | 'setups'
  | 'sheet_stock'
  | 'dxf'
  | 'sub_parts'
  | 'weld_ops'
  | 'secondary_ops'
  | 'finishing'
  | 'source'
  | 'markup'
  | 'review'
  | 'collar_nesting'
  | 'collar_cut'
  | 'outside_services';

const STEP_COMPONENTS: Record<StepId, (props: StepProps) => React.JSX.Element> = {
  identity: IdentityStep,
  material: MaterialStep,
  setups: SetupsStep,
  sheet_stock: SheetStockStep,
  dxf: DxfStep,
  sub_parts: SubPartsStep,
  weld_ops: WeldOpsStep,
  secondary_ops: SecondaryOpsStep,
  finishing: FinishingStep,
  source: SourceStep,
  markup: MarkupStep,
  review: ReviewStep,
  collar_nesting: CollarNestingStep,
  collar_cut: CollarCutStep,
  outside_services: OutsideServicesStep,
};

// Per-type wizard step ordering. Mirrors spec/14 "Part types" table + the
// v8 mockup's section 4. Each tuple is [stepId, displayName]. The displayName
// must match PART_TYPE_META.wizardSteps so the StepDots labels line up.
const STEP_FLOWS: Record<PartType, Array<{ id: StepId; name: string }>> = {
  machined: [
    { id: 'identity', name: 'Identity' },
    { id: 'material', name: 'Material' },
    { id: 'setups', name: 'Setups' },
    { id: 'outside_services', name: 'Outside services' },
    { id: 'finishing', name: 'Finishing' },
    { id: 'review', name: 'Review' },
  ],
  cut_blank: [
    { id: 'identity', name: 'Identity' },
    { id: 'sheet_stock', name: 'Sheet stock' },
    { id: 'dxf', name: 'DXF / Vendor' },
    { id: 'outside_services', name: 'Outside services' },
    { id: 'finishing', name: 'Finishing' },
    { id: 'review', name: 'Review' },
  ],
  weldment: [
    { id: 'identity', name: 'Identity' },
    { id: 'sub_parts', name: 'Sub-parts' },
    { id: 'weld_ops', name: 'Weld ops' },
    { id: 'outside_services', name: 'Outside services' },
    { id: 'finishing', name: 'Finishing' },
    { id: 'review', name: 'Review' },
  ],
  hybrid: [
    { id: 'identity', name: 'Identity' },
    { id: 'sheet_stock', name: 'Sheet stock' },
    { id: 'dxf', name: 'DXF' },
    { id: 'secondary_ops', name: 'Secondary ops' },
    { id: 'outside_services', name: 'Outside services' },
    { id: 'finishing', name: 'Finishing' },
    { id: 'review', name: 'Review' },
  ],
  purchased: [
    { id: 'source', name: 'Source' },
    { id: 'markup', name: 'Markup' },
    { id: 'review', name: 'Review' },
  ],
  collar: [
    { id: 'identity', name: 'Identity' },
    { id: 'material', name: 'Material' },
    { id: 'collar_nesting', name: 'Nesting' },
    { id: 'collar_cut', name: 'Cut time' },
    { id: 'outside_services', name: 'Outside services' },
    { id: 'finishing', name: 'Finishing' },
    { id: 'review', name: 'Review' },
  ],
  turninsulation: [
    { id: 'identity', name: 'Identity' },
    { id: 'material', name: 'Material' },
    { id: 'review', name: 'Review' },
  ],
};

export interface PartWizardProps {
  /** Caller-managed draft. Wizard is fully controlled. */
  part: Partial<Part> & { type: PartType };
  onChange: (updates: Partial<Part>) => void;
  /** Fired by the Review step's "Save part" action. Wizard hands the caller
   *  the (still-partial) draft; caller is responsible for filling in any
   *  defaults + persisting via crm.ts. */
  onSave: (part: Partial<Part> & { type: PartType }) => void | Promise<void>;
  /** Optional cancel handler (action row "Cancel" button). When omitted, the
   *  Cancel button is hidden. */
  onCancel?: () => void;
  /** Optional save-as-draft handler. When omitted, "Save as draft" is hidden
   *  on intermediate steps. */
  onSaveDraft?: (part: Partial<Part> & { type: PartType }) => void | Promise<void>;
  /** When true, render compact (no card outer frame) for embedding in
   *  modals / quote builders. Default false → standalone card. */
  embedded?: boolean;
}

export function PartWizard({
  part,
  onChange,
  onSave,
  onCancel,
  onSaveDraft,
  embedded = false,
}: PartWizardProps) {
  const flow = STEP_FLOWS[part.type];
  const meta = PART_TYPE_META[part.type];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [doneIndices, setDoneIndices] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  // Memoize for stable references through StepDots re-renders.
  const stepNames = useMemo(() => flow.map((s) => s.name), [flow]);

  // Clamp currentIndex if the flow length ever shrinks under us.
  const safeIndex = Math.min(currentIndex, flow.length - 1);
  const current = flow[safeIndex];
  const isLast = safeIndex === flow.length - 1;
  const isFirst = safeIndex === 0;
  const StepComponent = STEP_COMPONENTS[current.id];

  function markDone(idx: number) {
    setDoneIndices((prev) => (prev.includes(idx) ? prev : [...prev, idx]));
  }

  function goNext() {
    if (isLast) return;
    markDone(safeIndex);
    setCurrentIndex(safeIndex + 1);
  }

  function goBack() {
    if (isFirst) return;
    setCurrentIndex(safeIndex - 1);
  }

  function jumpTo(idx: number) {
    // Mark every step before idx as done so the dots read sensibly when
    // the user back-jumps after touching later steps.
    if (idx > safeIndex) {
      // Treat all skipped intermediate dots as done for the visual; the
      // caller's data layer is the source of truth on whether content is
      // valid (per spec/14 non-blocking warning approach).
      for (let i = safeIndex; i < idx; i++) markDone(i);
    }
    setCurrentIndex(idx);
  }

  async function handleSave() {
    setBusy(true);
    try {
      await onSave(part);
      // Mark Review as done after a successful save (mostly cosmetic — the
      // caller usually closes the wizard or routes away after onSave).
      markDone(safeIndex);
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveDraft() {
    if (!onSaveDraft) return;
    setBusy(true);
    try {
      await onSaveDraft(part);
    } finally {
      setBusy(false);
    }
  }

  // ─────────── content ───────────
  const headerLabel = part.number?.trim() || 'New Part';

  const inner = (
    <>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="text-sm font-semibold text-navy">
          <span aria-hidden>{meta.icon}</span>{' '}
          <span className="font-mono">{headerLabel}</span>
          <span className="text-text-mute"> · step {safeIndex + 1} of {flow.length}: </span>
          <span className="text-orange">{current.name}</span>
        </div>
        <PartTypeBadge type={part.type} size="md" />
      </div>

      <StepTabs
        steps={stepNames}
        currentIndex={safeIndex}
        doneIndices={doneIndices}
        onJump={jumpTo}
      />

      <div className="py-2">
        <StepComponent part={part} onChange={onChange} />
      </div>

      {/* Action row */}
      <div className="border-t border-line mt-4 pt-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          {!isFirst && (
            <button
              type="button"
              onClick={goBack}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-50"
            >
              ← Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isLast && onSaveDraft && (
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-50"
            >
              Save as draft
            </button>
          )}
          {!isLast && (
            <button
              type="button"
              onClick={goNext}
              disabled={busy}
              className="px-3 py-1.5 text-sm rounded border border-line-dark text-text-mute hover:bg-bg disabled:opacity-50"
              title="Move on without filling in this step's fields"
            >
              Skip step →
            </button>
          )}
          {!isLast && (
            <button
              type="button"
              onClick={goNext}
              disabled={busy}
              className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
            >
              Next: {flow[safeIndex + 1]?.name} →
            </button>
          )}
          {isLast && (
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
            >
              {busy ? 'Saving…' : 'Save part'}
            </button>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) {
    // Embedded mode (inside the QuoteDetailPage Builder tab): give the wizard
    // a clear visual boundary so operators can see where it starts/ends.
    //   - tinted bg + orange border + thick left accent strip
    //   - small orange header strip naming the part being edited
    const editingHeader = part.number?.trim()
      ? `EDITING PART · ${part.number.trim()}`
      : 'PART WIZARD';
    return (
      <div className="bg-bg/40 border-2 border-orange/40 border-l-4 border-l-orange rounded-lg shadow-sm p-4 my-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-orange mb-2">
          {editingHeader}
        </div>
        {inner}
      </div>
    );
  }

  return (
    <div className="bg-paper border-2 border-orange/60 rounded shadow-sm">
      <div className="px-4 py-2.5 bg-orange/5 border-b border-orange/20 text-sm font-semibold text-navy">
        Per-part wizard
      </div>
      <div className="p-4">{inner}</div>
    </div>
  );
}

// ─── StepTabs ─────────────────────────────────────────────────────────────
// Inline labeled-tab step indicator. Replaces the old numbered-circle
// StepDots so each step's name is visible without hovering. Pill row, wraps
// gracefully, all pills clickable (calls jumpTo).
//
// States:
//   - active   → orange bg + white text
//   - done     → green text + faint green border + ✓ glyph
//   - pending  → muted text
interface StepTabsProps {
  steps: string[];
  currentIndex: number;
  doneIndices: number[];
  onJump: (index: number) => void;
}

function StepTabs({ steps, currentIndex, doneIndices, onJump }: StepTabsProps) {
  const doneSet = new Set(doneIndices);
  return (
    <ol
      className="flex flex-wrap gap-1 mb-3"
      role="list"
      aria-label="Wizard steps"
    >
      {steps.map((stepName, i) => {
        const isActive = i === currentIndex;
        const isDone = !isActive && doneSet.has(i);

        let cls = 'px-3 py-1.5 rounded-full text-xs font-semibold transition-colors';
        if (isActive) {
          cls += ' bg-orange text-white';
        } else if (isDone) {
          cls += ' text-green border border-green/30 hover:bg-green/5';
        } else {
          cls += ' text-text-mute hover:text-navy hover:bg-bg';
        }

        return (
          <li key={`${stepName}-${i}`}>
            <button
              type="button"
              className={cls}
              onClick={() => onJump(i)}
              title={`${i + 1}. ${stepName}${isDone ? ' (done)' : isActive ? ' (current)' : ''}`}
              aria-current={isActive ? 'step' : undefined}
              aria-label={`Step ${i + 1}: ${stepName}${isDone ? ', done' : isActive ? ', current' : ''}`}
            >
              {isDone && <span aria-hidden>✓ </span>}
              {stepName}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
